<template>
    <div v-if="order" class="order-details">
        <v-row class="order-details__header show-scroll-up-mobile">
            <v-col cols="12" class="pb-0">
                <div class="text-h6 full-width align-center align-md-stretch d-flex flex-wrap flex-md-nowrap">
                    <div class="order-details__header-title-wrapper me-2 w-100 w-md-auto mr-2">
                        <div
                            class="order-details__title font-weight-bold text-nowrap"
                            :data-cy="`reservation-number-${order.orderId}`"
                        >
                            {{ `${$t('order')} #${order.orderId}` }}
                        </div>
                        <div v-if="order.parentOrderId">
                            <v-icon class="mx-1" color="grey-lighten-1" size="small">mdi-arrow-right-bold</v-icon>
                            <div class="font-weight-bold text-grey-lighten-1">
                                {{ ` #${order.parentOrderId}` }}
                            </div>
                        </div>
                    </div>
                    <div class="order-details__title order-last">
                        {{ order.title }}
                    </div>
                </div>
            </v-col>
        </v-row>
        <v-expand-transition>
            <v-row>
                <template v-if="itineraryViewServices.length">
                    <v-col v-if="$conf.public.account.itineraryCalendarTimeline" :cols="12">
                        <client-only>
                            <itinerary-calendar-timeline
                                :services="order.services"
                                @order-add-transfer="orderAddTransfer"
                            />
                        </client-only>
                    </v-col>
                    <v-col :cols="12" :md="6">
                        <div v-if="daysBeforeTrip > 0" class="text-success mb-2">
                            {{ `${daysBeforeTrip} ${$t('day', daysBeforeTrip)} ${$t('before_trip')} :)` }}
                        </div>
                        <v-slide-group>
                            <v-btn
                                v-for="day in itineraryDays"
                                :key="day.number"
                                :href="`${$route.path}#day-${day.number}`"
                            >
                                {{ `${$t('day', 1)}  ${day.number}` }}
                            </v-btn>
                        </v-slide-group>
                    </v-col>
                </template>
            </v-row>
        </v-expand-transition>
        <v-row>
            <v-col md="9">
                <div :class="{'order-wrapper': order.orderPackage}">
                    <order-details-traveler-list
                        v-if="$conf.public.account.orderTravelerList && tabSelectedName === 'info'"
                        :order-travelers="orderTravelers"
                        :order-end-date="orderEndDate"
                        :order="order"
                    />
                    <v-col v-if="$conf.public.account.tripPlans" cols="12">
                        <order-details-travelers-filter
                            :order-travelers="orderTravelers"
                            @change="selectedTravelerName = $event"
                        />
                    </v-col>
                    <v-alert
                        v-if="order.orderPackage"
                        border="bottom"
                        color="info"
                        class="text-white text-uppercase mb-0"
                        v-text="order.orderPackage.packageName"
                    />
                    <div class="px-2">
                        <div v-if="order.orderPackage">
                            <!--!!!-?-->
                            <component
                                :is="packageComponent"
                                :order="order"
                                @cancel-service="showCancelServiceModal($event)"
                                @cancel-tour="showCancelTourModal"
                                @book-all-quote="bookAllQuote"
                                @issue-ticket="$refs.issueTicketModal.show($event)"
                            />
                            <!--!!!-?-->
                        </div>
                        <v-card v-if="isShowCancelledItineraryMessage" variant="outlined">
                            <v-card-text>
                                <span class="text-warning font-weight-medium">
                                    {{ $t('trip_cancelled_itinerary') }}
                                </span>
                            </v-card-text>
                        </v-card>
                        <v-card v-if="isShowNoPlansItineraryMessage" variant="outlined">
                            <v-card-text>
                                <span class="text-warning font-weight-medium">
                                    {{ $t('no_plans_itinerary') }}
                                </span>
                            </v-card-text>
                        </v-card>
                        <itinerary-info
                            v-if="orderItinerary.description || orderItinerary.images"
                            :info="orderItinerary"
                        />
                        <div v-for="(itineraryDay, index) in itineraryDays" :key="index">
                            <order-destination
                                v-if="itineraryDestination(index)"
                                :destination="itineraryDestination(index)"
                                :destination-location="destinationLocation(itineraryDestination(index))"
                            />
                            <v-card :id="`day-${itineraryDay.number}`" variant="outlined" class="mb-2">
                                <v-alert color="grey-lighten-4" class="text-black mb-0">
                                    <div class="d-flex justify-space-between">
                                        <div class="d-flex">
                                            <strong class="text-body-1 text-capitalize me-2 font-weight-medium">
                                                <v-icon>mdi-weather-sunny</v-icon>
                                                {{ $t('day', 1) }} {{ itineraryDay.number }}
                                            </strong>
                                            <span class="text-body-1 text-capitalize">
                                                {{ $filters.dateFormat(itineraryDay.date) }}
                                            </span>
                                        </div>
                                        <weather-service
                                            v-if="itineraryDay.services"
                                            :city="{
                                                id: findAccommodationService(itineraryDay.services.times, 'cityId'),
                                                cityName: findAccommodationService(
                                                    itineraryDay.services.times,
                                                    'cityName'
                                                ),
                                            }"
                                            :start-date="itineraryDay.services.date"
                                            show-single-day
                                        />
                                    </div>
                                </v-alert>
                            </v-card>
                            <v-row class="mb-0">
                                <v-col cols="9" v-html="itineraryDay.description && itineraryDay.description[locale]" />
                                <v-col cols="3">
                                    <order-itinerary-day-images
                                        :images="itineraryDayImages(itineraryDay.number)"
                                        @show-photo-swipe="showPhotoSwipe($event)"
                                    />
                                </v-col>
                            </v-row>
                            <template v-if="itineraryDay.services">
                                <div
                                    v-for="(time, timeIndex) in itineraryDay.services.times"
                                    :key="time + timeIndex"
                                    class="d-md-flex"
                                >
                                    <div class="mr-4 text-subtitle-1 font-weight-regular time">
                                        <span v-if="time.type.text">
                                            {{ time.type.text() }}
                                        </span>
                                        <span v-if="time.type.textFlight"> {{ time.type.textFlight() }}&nbsp; </span>
                                        <span v-if="time.time && time.time !== '00:00'">
                                            {{ time.time }}
                                        </span>
                                    </div>
                                    <div class="flex-grow-1">
                                        <template
                                            v-for="(service, i) in time.service"
                                            :key="`${service.serviceId}-${i}`"
                                        >
                                            <component
                                                :is="serviceComponent(service.serviceType)"
                                                v-if="
                                                    serviceFilterBySelectedTraveler(service) &&
                                                    serviceFilterBySelectedVariant(service)
                                                "
                                                :service="service"
                                                :service-tips="
                                                    tips && tips.filter(t => t.serviceId === service.serviceId)
                                                "
                                                :itinerary-service="time"
                                                :is-package="!!service.serviceGroupId"
                                                class="mb-5"
                                                :order="order"
                                                :order-id="order.orderId"
                                                :object-id="order.objectId"
                                                :order-end-date="orderEndDate"
                                                :data-cy="`service-${i}`"
                                                :variants-available="variantsAvailable"
                                                @cancel-service="showCancelServiceModal($event)"
                                                @issue-ticket="$refs.issueTicketModal.show($event)"
                                                @modify-service="$emit('modify-service')"
                                                @add-marker="addMarker"
                                                @info-loaded="info = $event"
                                                @show-on-map="showOnMap"
                                            />
                                        </template>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <template v-if="servicesOrderInited">
                            <component
                                :is="serviceComponent(service.serviceType)"
                                v-for="(service, index) in packageServices"
                                :key="service.serviceId"
                                :service="service"
                                :is-package="!!order.orderPackage"
                                class="mb-5"
                                :service-tips="tips && tips.filter(t => t.serviceId === service.serviceId)"
                                :order="order"
                                :order-id="order.orderId"
                                :object-id="order.objectId"
                                :is-first="index === 0"
                                :order-end-date="orderEndDate"
                                :data-cy="`service-${index}`"
                                :variants-available="variantsAvailable"
                                @cancel-service="showCancelServiceModal($event)"
                                @issue-ticket="$refs.issueTicketModal.show($event)"
                                @add-marker="addMarker"
                                @modify-service="$emit('modify-service')"
                                @info-loaded="info = $event"
                                @show-on-map="showOnMap"
                            />
                        </template>
                    </div>
                </div>
            </v-col>
            <v-col v-if="!$breakpoint.smAndDown" class="order-details__sidebar" md="3">
                <div class="order-summary">
                    <order-details-summary
                        v-if="isTripAssistantKeyExist"
                        class="mb-4"
                        :checkbox-services-selected="checkboxServicesSelected"
                        :order-travelers="orderTravelers"
                        :vat-calculated-amount-by-each-service-brutto="vatCalculatedAmountByEachServiceBrutto"
                        :vat-calculated-amount-by-each-service-netto="vatCalculatedAmountByEachServiceNetto"
                        :order-prices-calculated-brutto="bruttoPrice"
                        :order-prices-calculated-netto="nettoPrice"
                    />
                    <order-details-trip-assistant-summary
                        v-else
                        :checkbox-services-selected="checkboxServicesSelected"
                        :order-travelers="orderTravelers"
                        class="mb-4"
                    />
                    <weather-service
                        v-if="order.services[0]"
                        :city="{
                            id: order.services[0].serviceDetails[0].cityId,
                            cityName: order.services[0].serviceDetails[0].cityName,
                        }"
                        :start-date="weatherStartDate"
                        :end-date="weatherEndDate"
                        class="mb-4"
                    />
                    <client-only>
                        <v-card v-if="isShowMap && !isMobile" class="order-details__map mb-4" variant="outlined">
                            <v-card-text>
                                <order-details-map
                                    :marker-coordinates="orderDetailsMapMarkers"
                                    :destinations="destinationsSortedByDate"
                                    :destination-locations="destinationLocations"
                                />
                            </v-card-text>
                        </v-card>
                    </client-only>
                </div>
            </v-col>
        </v-row>
        <conditions-modal disable-currency-conversion />
        <v-photo-swipe
            v-if="photoSwipeItems?.length"
            ref="photoSwipe"
            :items="photoSwipeItems"
            :photo-swipe-options="photoSwipeOptions"
        />
        <client-only>
            <v-dialog v-model="mapModal" :max-width="500">
                <map-location :latitude="mapPoint.latitude" :longitude="mapPoint.longitude" :height="`500px`" />
            </v-dialog>
        </client-only>
        <contact-details-modal />
    </div>
</template>

<script lang="ts">
    import {Component, Provide, Inject} from 'vue-facing-decorator'
    import __OrderDetails, {OrderDetails as _OrderDetails} from '@/components/account/OrderDetails'

    import MapLocation from '~src/components/info/mapLocation.src'
    import ContactDetailsModal from '@/components/account/modals/ContactDetailsModal.vue'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import OrderDestination from '~/components/account/OrderDestination.vue'
    import OrderDetailsTripAssistantSummary from '~/components/account/OrderDetailsTripAssistantSummary.vue'
    import OrderDetailsTravelerList from '~/components/account/OrderDetailsTravelerList.vue'
    import OrderDetailsMap from '~/components/account/OrderDetailsMap.vue'
    import PriceLabel from '~/components/snippets/PriceLabel.vue'
    import OrderItineraryDayImages from '~/components/account/snippets/OrderItineraryDayImages.vue'
    import ItineraryInfo from '~/components/info/ItineraryInfo.vue'
    import OrderPrice from '~/components/account/snippets/OrderPrice.vue'
    import TotalOrderPrices from '~/components/account/snippets/TotalOrderPrices.vue'
    import CancelServiceModal from '~/components/account/modals/CancelServiceModal.vue'
    import FlightService from '~/components/account/orderServices/FlightService.vue'
    import AccommodationService from '~/components/account/orderServices/AccommodationService.vue'
    import TransferService from '~/components/account/orderServices/TransferService.vue'
    import CarService from '~/components/account/orderServices/CarService.vue'
    import InsuranceService from '~/components/account/orderServices/InsuranceService.vue'
    import TourService from '~/components/account/orderServices/TourService.vue'
    import ActivityService from '~/components/account/orderServices/ActivityService.vue'
    import VisaService from '~/components/account/orderServices/VisaService.vue'
    import PackageInfo from '~/components/account/orderServices/PackageInfo.vue'
    import PackageTourInfo from '~/components/account/orderServices/PackageTourInfo.vue'
    import ExtraserviceService from '~/components/account/orderServices/ExtraserviceService.vue'
    import OfflineService from '~/components/account/orderServices/OfflineService.vue'
    import VPhotoSwipe from '~/components/snippets/VPhotoSwipe.vue'
    import PaymentStatusLabel from '~/components/account/snippets/PaymentStatusLabel.vue'
    import WeatherService from '~/components/WeatherService.vue'
    import OrdersPriceLabel from '~/components/account/snippets/OrdersPriceLabel.vue'
    import CruiseService from '~/components/account/orderServices/CruiseService.vue'
    import OrderDetailsSummary from '~src/components/account/orderDetailsSummary.src'
    @Component({
        mixins: [__OrderDetails],
        components: {
            ItineraryCalendarTimeline: defineAsyncComponent(
                () => import('@/components/account/ItineraryCalendarTimeline.vue')
            ),
            MapLocation,
            ContactDetailsModal,
            ConditionsModal,
            OrderDestination,
            OrderDetailsTripAssistantSummary,
            OrderDetailsTravelerList,
            OrderDetailsMap,
            PriceLabel,
            OrderItineraryDayImages,
            ItineraryInfo,
            OrderPrice,
            TotalOrderPrices,
            CancelServiceModal,
            FlightService,
            AccommodationService,
            TransferService,
            CarService,
            InsuranceService,
            TourService,
            ActivityService,
            VisaService,
            PackageInfo,
            PackageTourInfo,
            ExtraserviceService,
            OfflineService,
            VPhotoSwipe,
            PaymentStatusLabel,
            WeatherService,
            OrdersPriceLabel,
            CruiseService,
            OrderDetailsSummary,
        },
    })
    export class OrderDetailsItineraryInfo extends _OrderDetails {
        @Provide() isReadOnly = true
        @Inject({default: false}) isTripAssistantKeyExist
        displayFormat = 'itineraryView'
        mapModal = false
        mapPoint = {}

        showOnMap($event) {
            this.mapPoint = $event
            this.mapModal = true
        }
    }
    export default OrderDetailsItineraryInfo
</script>
<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';

    :deep(.v-slide-group__next--disabled),
    :deep(.v-slide-group__prev--disabled) {
        display: none;
    }

    .commission {
        font-size: 10px;
        line-height: 1rem;
    }

    .display-format-label {
        font-size: 13px;
        color: rgba(0, 0, 0, 0.6);
    }

    @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
        .time {
            width: 90px;
        }
    }

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
        .order-details__header-title-wrapper {
            order: unset !important;
        }
    }

    .order-wrapper {
        border: 3px solid rgb(52, 150, 243);
        border-radius: 5px;
        margin-bottom: 10px;

        & .services {
            padding: 10px;
        }
    }

    @media (min-width: 991px) {
        .order-summary {
            position: sticky;
            top: 0;
        }
    }

    .view-summary-button {
        position: sticky;
        bottom: 0;
        z-index: 200;
    }

    :deep(.order-summary) .commission-block {
        font-size: 12px !important;
    }

    :deep(.selected-service-modal .service-card) {
        margin-bottom: 0 !important;
    }

    table .th-footer {
        vertical-align: top;
    }

    :deep(.v-data-table.sortable) table tbody tr,
    :deep(.sortable) .service-card > .v-alert {
        cursor: pointer;
    }

    @media (max-width: 960px) {
        :deep(.table) tbody .v-data-table__mobile-row__cell {
            width: 100%;
        }
    }
</style>
